import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import { Breadcrumbs, type Crumb } from "./Breadcrumbs";
import { SectionTitle } from "./blocks";

/**
 * Shared page primitives for every Turkish public sub-page.
 * They reproduce the homepage / theme-page system: breadcrumb, dot kicker,
 * large serif title, serif lede, hairline-separated sections, 7xl grid.
 */
export const WRAP = "mx-auto w-full max-w-7xl px-5 sm:px-8";

export function Kicker({ children, dot = "bg-ink", className = "" }: { children: ReactNode; dot?: string | null; className?: string }) {
  return (
    <p className={`flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-ink/80 ${className}`}>
      {dot && <span aria-hidden className={`size-2.5 shrink-0 rounded-full ${dot}`} />}
      <span>{children}</span>
    </p>
  );
}

export function PageIntro({
  crumbs,
  kicker,
  dot,
  title,
  lede,
  aside,
  children,
}: {
  crumbs: Crumb[];
  kicker?: ReactNode;
  dot?: string | null;
  title: ReactNode;
  lede?: ReactNode;
  aside?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className={`${WRAP} pt-5 lg:pt-7`}>
      <Breadcrumbs items={crumbs} />
      <div className={`mt-5 grid gap-6 pb-8 lg:mt-8 lg:gap-10 lg:pb-12 ${aside ? "lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-center" : ""}`}>
        <div className="min-w-0">
          {kicker && <Kicker dot={dot}>{kicker}</Kicker>}
          <h1 className="mt-3 max-w-4xl text-balance font-serif text-[40px] font-medium leading-[1] sm:text-[56px] lg:text-[68px]">{title}</h1>
          <span aria-hidden className={`mt-5 block h-1 w-12 ${dot ?? "bg-ink"}`} />
          {lede && <div className="mt-5 max-w-2xl font-serif text-[19px] leading-snug text-ink/80 sm:text-[22px]">{lede}</div>}
          {children}
        </div>
        {aside && <div className="min-w-0">{aside}</div>}
      </div>
    </section>
  );
}

export function PageSection({
  id,
  title,
  aside,
  link,
  children,
  tone = "plain",
  className = "",
}: {
  id?: string;
  title?: string;
  aside?: ReactNode;
  link?: ReactNode;
  children: ReactNode;
  tone?: "plain" | "muted";
  className?: string;
}) {
  const headingId = id ? `${id}-baslik` : undefined;
  return (
    <section id={id} aria-labelledby={title ? headingId : undefined} className={`scroll-mt-6 border-t border-ink/10 ${tone === "muted" ? "bg-secondary/60" : ""}`}>
      <div className={`${WRAP} py-8 lg:py-12 ${className}`}>
        {title && <SectionTitle id={headingId} title={title} aside={aside} link={link} />}
        <div className={title ? "mt-5 lg:mt-7" : ""}>{children}</div>
      </div>
    </section>
  );
}

/** Long-form reading column, same rhythm as article body. */
export function Prose({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`max-w-[68ch] space-y-4 text-[16px] leading-relaxed text-ink/85 sm:text-[17px] [&_a]:text-theme-tech [&_a]:underline [&_h3]:pt-2 [&_h3]:font-serif [&_h3]:text-xl [&_strong]:text-ink ${className}`}>{children}</div>;
}

export function MoreLink({ to, params, href, children }: { to?: string; params?: Record<string, string>; href?: string; children: ReactNode }) {
  const cls = "inline-flex min-h-11 items-center gap-2 font-serif text-[15px] text-theme-tech hover:underline";
  if (href) {
    const external = /^https?:/.test(href);
    return (
      <a href={href} className={cls} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
        {children} <ArrowRight aria-hidden className="size-4" />
      </a>
    );
  }
  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Link to={to as any} params={params as any} className={cls}>
      {children} <ArrowRight aria-hidden className="size-4" />
    </Link>
  );
}

export function Chip({ active, onClick, dot, children, count }: { active?: boolean; onClick?: () => void; dot?: string; children: ReactNode; count?: number }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border px-4 text-[13px] transition-colors ${
        active ? "border-ink bg-ink text-paper" : "border-ink/20 text-ink/80 hover:border-ink hover:text-ink"
      }`}
    >
      {dot && <span aria-hidden className={`size-2 rounded-full ${dot}`} />}
      {children}
      {count !== undefined && <span className={active ? "text-paper/70" : "text-ink/50"}>{count}</span>}
    </button>
  );
}

/** Row list for any link item (posts, notes, issues, reports, talks). */
export type RowItem = {
  key: string;
  to?: string;
  params?: Record<string, string>;
  href?: string;
  kicker?: ReactNode;
  title: string;
  excerpt?: string;
  meta?: ReactNode;
  img?: string | null;
};

export function LinkRows({ items, withExcerpt = true }: { items: RowItem[]; withExcerpt?: boolean }) {
  return (
    <ul className="divide-y divide-ink/10 border-y border-ink/10">
      {items.map((it) => {
        const body = (
          <>
            {it.img !== undefined && (
              it.img ? (
                <img src={it.img} alt="" loading="lazy" decoding="async" width={200} height={125} className="h-14 w-20 object-cover sm:h-[72px] sm:w-[116px]" />
              ) : (
                <span aria-hidden className="block h-14 w-20 bg-muted sm:h-[72px] sm:w-[116px]" />
              )
            )}
            <span className="min-w-0">
              {it.kicker && <span className="block">{it.kicker}</span>}
              <span className="mt-1 block font-serif text-[17px] leading-snug text-ink group-hover:text-clay sm:text-[20px]">{it.title}</span>
              {withExcerpt && it.excerpt && <span className="mt-1.5 hidden text-sm leading-snug text-ink/70 sm:line-clamp-2">{it.excerpt}</span>}
              {it.meta && <span className="mt-1.5 block text-xs text-ink/60">{it.meta}</span>}
            </span>
            <ArrowRight aria-hidden className="size-4 shrink-0 text-ink transition-transform group-hover:translate-x-1" />
          </>
        );
        const cls = `group grid items-center gap-x-4 py-4 ${it.img !== undefined ? "grid-cols-[auto_minmax(0,1fr)_auto]" : "grid-cols-[minmax(0,1fr)_auto]"}`;
        return (
          <li key={it.key}>
            {it.href ? (
              <a href={it.href} className={cls} {...(/^https?:/.test(it.href) ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{body}</a>
            ) : (
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              <Link to={it.to as any} params={it.params as any} className={cls}>{body}</Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function Pager({ page, pages, onChange }: { page: number; pages: number; onChange: (p: number) => void }) {
  if (pages <= 1) return null;
  const btn = "inline-flex size-11 items-center justify-center rounded-full border text-sm transition-colors";
  return (
    <nav aria-label="Sayfalar" className="mt-8 flex flex-wrap items-center justify-center gap-2">
      <button type="button" disabled={page <= 1} onClick={() => onChange(page - 1)} aria-label="Önceki sayfa" className={`${btn} border-ink/20 disabled:opacity-30`}>
        <ArrowLeft aria-hidden className="size-4" />
      </button>
      {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          aria-current={n === page ? "page" : undefined}
          className={`${btn} ${n === page ? "border-ink bg-ink text-paper" : "border-ink/20 hover:border-ink"}`}
        >
          {n}
        </button>
      ))}
      <button type="button" disabled={page >= pages} onClick={() => onChange(page + 1)} aria-label="Sonraki sayfa" className={`${btn} border-ink/20 disabled:opacity-30`}>
        <ArrowRight aria-hidden className="size-4" />
      </button>
    </nav>
  );
}

/** Small boxed card used for facts, links and highlights (no heavy borders). */
export function InfoCard({ title, children, className = "" }: { title?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={`bg-secondary p-5 sm:p-6 ${className}`}>
      {title && <h3 className="font-serif text-xl font-medium">{title}</h3>}
      <div className={title ? "mt-3" : ""}>{children}</div>
    </div>
  );
}
